import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Skeleton,
  Typography,
  Button,
} from "@mui/material";
import { IconSparkles, IconCopy } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { Moment } from "moment";
import { onSnapshot } from "firebase/firestore";
import AnaliseInteligenteMensalDb from "../../../db/analiseInteligenteMensalDb";
import AnaliseInteligenteMensal from "../../../models/analiseInteligenteMensal";
import { useUsuario } from "../../../providers/usuarioProvider";
import { LoadingButton } from "@mui/lab";
import Functions from "../../../callable/functions";
import felixAnalytics from "../../../felixAnalytics";
import { useSnackBar } from "../../../stores/snackBarStores";
import { useNavigate } from "react-router-dom";

type Props = {
  onClose: (success: boolean) => void;
  open: boolean;
  data: Moment;
};

export default function AnaliseIADialog({ onClose, open, data }: Props) {
  const navigate = useNavigate();
  const { visualizado } = useUsuario();
  const snackbar = useSnackBar();
  const [loading, setLoading] = useState(true);
  const [relatorio, setRelatorio] = useState<AnaliseInteligenteMensal>();
  const [gerando, setGerando] = useState(false);

  useEffect(() => {
    if (!!visualizado) {
      return onSnapshot(
        new AnaliseInteligenteMensalDb().doc({
          usuarioId: visualizado,
          analiseId: data.format("YYYY-MM"),
        }),
        (snapshot) => {
          if (snapshot.exists()) setRelatorio(snapshot.data());
          setLoading(false);
        }
      );
    }
  }, [visualizado]);

  const handleCopyToClipboard = () => {
    const fullAnalysis = `
      *Análise Inteligente de ${data.format("MMMM [de] YYYY")}*

      ${relatorio?.introducao}

      *Resultados e Impostos*
      ${relatorio?.resultados}

      *Carteira*
      ${relatorio?.carteira}

      *Rendimentos*
      ${relatorio?.proventos}

      ${relatorio?.conclusao}
    `;
    navigator.clipboard.writeText(fullAnalysis.trim());
    snackbar({
      type: "ALERT",
      payload: {
        mensagem: "Relatório copiado para sua área de transferência.",
        severidade: "success",
      },
    });
  };

  return (
    <Dialog onClose={onClose} open={open} maxWidth="md" fullWidth>
      <DialogTitle color="text.primary">
        <Box display="flex" flexDirection="row" alignItems="center">
          <Box mr={1} display="flex" color="tertiary.main">
            <IconSparkles size={16} />
          </Box>
          <Typography variant="h3">
            {`Análise Inteligente de ${data.format("MMMM [de] YYYY")}`}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent
        sx={{
          py: 1,
          // Scrollbar na rampa de tinta — funciona nos dois temas.
          "&::-webkit-scrollbar": {
            width: 12,
          },
          "&::-webkit-scrollbar-track": {
            background: (theme) => theme.palette.ink["08"],
            backgroundClip: "padding-box !important",
            borderLeft: "4px transparent solid",
          },
          "&::-webkit-scrollbar-thumb": {
            background: (theme) => theme.palette.ink[20],
            backgroundClip: "padding-box !important",
            borderLeft: "4px transparent solid",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            background: (theme) => theme.palette.ink[40],
          },
        }}
      >
        {loading ? (
          <>
            <Skeleton height={400} />
          </>
        ) : !!relatorio ? (
          <>
            <Typography variant="body1" color="text.secondary" mb={2}>
              {relatorio?.introducao}
            </Typography>
            <Typography variant="eyebrow" color="text.primary" mt={3} mb={1}>
              Resultados e Impostos
            </Typography>
            <Typography variant="body1" color="text.secondary" mb={2}>
              {relatorio?.resultados}
            </Typography>
            <Typography variant="eyebrow" color="text.primary" mt={3} mb={1}>
              Carteira
            </Typography>
            <Typography variant="body1" color="text.secondary" mb={2}>
              {relatorio?.carteira}
            </Typography>
            <Typography variant="eyebrow" color="text.primary" mt={3} mb={1}>
              Rendimentos
            </Typography>
            <Typography variant="body1" color="text.secondary" mb={2}>
              {relatorio?.proventos}
            </Typography>
            <Typography variant="body1" color="text.secondary" mb={2}>
              {relatorio?.conclusao}
            </Typography>
            <Divider sx={{ mt: 2 }} />
            <Box
              display="flex"
              flexDirection="column"
              pt={2}
              alignItems="center"
              width="100%"
            >
              <Typography
                alignSelf="center"
                variant="body2"
                color="text.secondary"
                textAlign="center"
              >
                Informações geradas por Inteligência Artificial podem conter
                imprecisões.
                <br />
                Lembre-se de revisar tudo antes de tomar decisões.
              </Typography>
            </Box>
          </>
        ) : (
          <DialogContentText>
            <Typography
              variant="h3"
              component="span"
              display="block"
              color="text.primary"
              gutterBottom
            >
              Análise gerada por Inteligência Artificial
            </Typography>
            Com as Análises Inteligentes, gere análises detalhadas e
            personalizadas de investimentos, utilizando a mais avançada
            tecnologia de Inteligência Artificial. A IA da Felix na Bolsa
            oferece insights valiosos para otimizar estratégia de investimento e
            garantir economia de impostos de maneira prática e automatizada.
          </DialogContentText>
        )}
      </DialogContent>
      <DialogActions>
        {!!relatorio ? (
          <Button
            variant="text"
            startIcon={<IconCopy />}
            onClick={handleCopyToClipboard}
            sx={{ color: "accentInk" }}
          >
            Copiar Relatório
          </Button>
        ) : (
          <></>
        )}
        <LoadingButton
          variant="contained"
          color="tertiary"
          loading={gerando}
          loadingPosition="start"
          startIcon={<IconSparkles />}
          onClick={async () => {
            setGerando(true);
            felixAnalytics.track("analise-inteligente");
            await new Functions()
              .analiseInteligenteMensal({ data: data.format() })
              .catch((error) => {
                if (error.details?.codErro === 39) {
                  navigate("/creditos");
                } else {
                  snackbar({
                    type: "ALERT",
                    payload: {
                      mensagem:
                        "Ocorreu um erro ao realizar a análise inteligente.",
                      severidade: "warning",
                    },
                  });
                }
                console.error(error);
              })
              .finally(() => setGerando(false));
          }}
        >
          Gerar Análise (10 créditos)
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
